import React, { useState, useMemo } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "typeface-inter";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { customTheme, darkTheme } from "./theme";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { appInsights } from "./appInsights";
import { msalInstance } from "./common/interceptor/msal-instance";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getPreference, setPreference } from "./common/services/cookie-service";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Add global styles for font scaling
const style = document.createElement("style");
style.textContent = `
  :root {
    font-size: 16px;
  }
  html {
    font-size: 100%;
  }
  body {
    font-size: 1rem;
  }
`;
document.head.appendChild(style);

// Initialize Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // ⛔ Disable retry globally
    },
  },
});

const RootComponent = () => {
  const [isDarkMode, setIsDarkMode] = useState(getPreference("darkMode", false));

  // Toggle theme and store preference
  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    setPreference("darkMode", newMode); // Store user preference
  };

  // Apply selected theme
  const theme = useMemo(
    () => (isDarkMode ? darkTheme : customTheme),
    [isDarkMode]
  );

  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <App toggleTheme={toggleTheme} isDarkMode={isDarkMode} />
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </MsalProvider>
  );
};

// Use the new React 18 root API
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <AppInsightsContext.Provider value={appInsights}>
    <RootComponent />
  </AppInsightsContext.Provider>
);

// Report web vitals
reportWebVitals();

// Register the service worker for PWA functionality
serviceWorkerRegistration.register({
  onSuccess: () => {
    console.log('RackStar is now available offline and can be installed as an app!');
  },
  onUpdate: (registration) => {
    console.log('New version available! Please refresh the page.');
    // You can add a notification here to inform users of updates
  }
});